import {
  type AriaAttributes,
  cloneElement,
  createContext,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useId,
  useState,
} from 'react';
import { Offcanvas } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import './style.scss';
import { SupportProductList } from 'routes/smart-quote/components/disclaimer/supported-product-list';

const PRIVACY_POLICY_URL = 'https://content.sgmarkets.com/PP_AND_COOKIE_CHARTER';

type DisclaimerCloseReason = 'x button' | 'dismiss button' | 'escape button';

export interface DisclaimerContext {
  opened: boolean;
  open: () => void;
  close: (reason: DisclaimerCloseReason) => void;

  panelId: string;
}

const disclaimerContext = createContext<DisclaimerContext | null>(null);

function useDisclaimer() {
  const ctx = useContext(disclaimerContext);
  if (ctx === null) {
    throw new Error('useDisclaimer must be used within <DisclaimerProvider>');
  }
  return ctx;
}

export function DisclaimerProvider({
  children,
  onClose,
}: {
  children: ReactNode;
  onClose?: (reason: DisclaimerCloseReason) => void;
}) {
  const [opened, setOpened] = useState(false);
  const panelId = useId();

  const open = useCallback(() => setOpened(true), []);
  const close = useCallback(
    (reason: DisclaimerCloseReason) => {
      setOpened(false);
      onClose?.(reason);
    },
    [onClose],
  );

  return (
    <disclaimerContext.Provider value={{ opened, open, close, panelId }}>
      {children}
      <DisclaimerPanel />
    </disclaimerContext.Provider>
  );
}

type TriggerChildProps = { onClick?: () => void } & Pick<
  AriaAttributes,
  'aria-controls' | 'aria-expanded' | 'aria-haspopup'
>;

export function DisclaimerTrigger({ children }: { children: ReactElement<TriggerChildProps> }) {
  const { opened, open, panelId } = useDisclaimer();
  return cloneElement(children, {
    onClick: () => {
      if (!opened) {
        children?.props?.onClick?.();
        open();
      }
    },
    'aria-controls': panelId,
    'aria-expanded': opened,
    'aria-haspopup': 'dialog',
  });
}

function DisclaimerPanel() {
  const { opened, close, panelId } = useDisclaimer();
  const handleClose = (reason: DisclaimerCloseReason) => () => close(reason);
  return (
    <Offcanvas
      id={panelId}
      show={opened}
      onHide={handleClose('escape button')}
      placement="end"
      backdrop={false}
      scroll={true}
      enforceFocus={false}
      role="complementary"
      className="border-start border-opacity-70 shadow-none smart-quote-disclaimer"
    >
      <Offcanvas.Header className="offcanvas-header p-0">
        <div className="flex-grow-1 flex-x gap-2 p-2">
          <h1 className="h5 px-12px">
            <FormattedMessage id="smartQuote.disclaimer.title" />
          </h1>
        </div>
        <div className="p-2">
          <button className="btn btn-flat-secondary btn-lg btn-icon" type="button" onClick={handleClose('x button')}>
            <i className="icon">clear</i>
          </button>
        </div>
      </Offcanvas.Header>

      <Offcanvas.Body className="overflow-auto scrollbar-sm flex-y gap-3 px-4">
        <p>
          <FormattedMessage
            id="smartQuote.disclaimer.paragraph1"
            values={{
              privacyPolicyLink: txt => (
                <a href={PRIVACY_POLICY_URL} className="text-link" target="_blank" rel="noopener noreferrer">
                  {txt}
                </a>
              ),
            }}
          />
        </p>
        <p>
          <FormattedMessage id="smartQuote.disclaimer.paragraph2" />
        </p>
        <p>
          <FormattedMessage id="smartQuote.disclaimer.paragraph3" />
        </p>
        <p>
          <FormattedMessage id="smartQuote.disclaimer.paragraph4" />
        </p>
        <SupportProductList className="py-1" />
      </Offcanvas.Body>
      <div className="offcanvas-footer p-4">
        <button
          className="btn btn-discreet-secondary btn-lg btn-block"
          type="button"
          onClick={handleClose('dismiss button')}
        >
          <FormattedMessage id="smartQuote.disclaimer.dismiss" />
        </button>
      </div>
    </Offcanvas>
  );
}
